<!-- Onboarding Wizard Modal -->
<div
  *ngIf="isOpen()"
  class="modal show d-block"
  tabindex="-1"
  role="dialog"
  (click)="onBackdropClick($event)"
  (keydown)="onKeydown($event)">
  <div class="modal-dialog modal-lg modal-dialog-centered">
    <div class="modal-content">
      <!-- Modal Header -->
      <div class="modal-header">
        <div>
          <h5 class="modal-title">Complete Your Onboarding</h5>
          <p class="text-muted small mb-0">
            Step {{ currentStep() }} of {{ totalSteps() }}: {{ currentStepInfo().title }}
          </p>
        </div>
        <button
          type="button"
          class="btn-close"
          aria-label="Close"
          (click)="close()"
          [disabled]="isProcessing()">
        </button>
      </div>

      <!-- Progress Indicator -->
      <div class="progress rounded-0 h-4px">
        <div
          class="progress-bar bg-primary"
          role="progressbar"
          [style.width.%]="(currentStep() / totalSteps()) * 100"
          [attr.aria-valuenow]="currentStep()"
          [attr.aria-valuemin]="1"
          [attr.aria-valuemax]="totalSteps()">
        </div>
      </div>

      <!-- Modal Body -->
      <div class="modal-body">
        <!-- Step 1: Complete Profile -->
        <div *ngIf="currentStep() === 1" class="step-content">
          <div class="step-header mb-3">
            <h6>{{ currentStepInfo().title }}</h6>
            <p class="text-muted">{{ currentStepInfo().subtitle }}</p>
          </div>

          <form [formGroup]="profileForm">
            <div class="row g-3">
              <div class="col-md-6">
                <label for="phone" class="form-label">Phone Number <span class="text-danger">*</span></label>
                <input
                  type="tel"
                  class="form-control"
                  id="phone"
                  formControlName="phone"
                  mask="(000) 000-0000"
                  [validation]="false"
                  placeholder="(555) 123-4567">
              </div>
              <div class="col-md-6">
                <label for="dateOfBirth" class="form-label">Date of Birth <span class="text-muted">(optional)</span></label>
                <input
                  type="date"
                  class="form-control"
                  id="dateOfBirth"
                  formControlName="dateOfBirth"
                  max="9999-12-31">
              </div>
              <div class="col-12">
                <label for="addressLine1" class="form-label">Address Line 1 <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="addressLine1"
                  formControlName="addressLine1"
                  placeholder="123 Main St">
              </div>
              <div class="col-12">
                <label for="addressLine2" class="form-label">Address Line 2</label>
                <input
                  type="text"
                  class="form-control"
                  id="addressLine2"
                  formControlName="addressLine2"
                  placeholder="Apt, Suite, Unit, etc.">
              </div>
              <div class="col-md-5">
                <label for="city" class="form-label">City <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="city"
                  formControlName="city">
              </div>
              <div class="col-md-3">
                <label for="state" class="form-label">State <span class="text-danger">*</span></label>
                <app-us-state-select inputId="state" formControlName="state" display="both"></app-us-state-select>
              </div>
              <div class="col-md-4">
                <label for="zipCode" class="form-label">Zip Code <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="zipCode"
                  formControlName="zipCode"
                  maxlength="10">
              </div>
            </div>
          </form>
        </div>

        <!-- Step 2: Upload Part 107 Certificate -->
        <div *ngIf="currentStep() === 2" class="step-content">
          <div class="step-header mb-3">
            <div class="d-flex align-items-center">
              <i class="bi bi-exclamation-triangle-fill text-warning me-2"></i>
              <h6 class="mb-0">{{ currentStepInfo().title }}</h6>
            </div>
            <p class="text-muted mt-2">{{ currentStepInfo().subtitle }}</p>
            <div class="alert alert-info small">
              <i class="bi bi-info-circle me-1"></i>
              This certificate is required by the FAA for all commercial drone operations under Part 107.
            </div>
          </div>

          <form [formGroup]="part107Form">
            <div class="row g-3">
              <div class="col-md-6">
                <label for="part107Number" class="form-label">Certificate Number <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="part107Number"
                  formControlName="certificateNumber"
                  placeholder="4012345">
              </div>
              <div class="col-md-6">
                <label for="part107Expiration" class="form-label">Currency Expiration <span class="text-danger">*</span></label>
                <input
                  type="date"
                  class="form-control"
                  id="part107Expiration"
                  formControlName="expirationDate"
                  max="9999-12-31"
                  >
                <small class="text-muted">24 months from initial test or last recurrent training</small>
              </div>
              <div class="col-12">
                <label for="part107File" class="form-label">
                  Upload Certificate (PDF, JPG, PNG)
                  <span *ngIf="!existingPart107Doc()" class="text-danger">*</span>
                </label>
                <!-- Show existing document if present -->
                <div *ngIf="existingPart107Doc() && !part107File()" class="alert alert-success py-2 mb-2">
                  <div class="d-flex align-items-center justify-content-between">
                    <div>
                      <i class="bi bi-file-earmark-check me-2"></i>
                      <span class="small">{{ existingPart107Doc()!.fileName }}</span>
                    </div>
                    <a [href]="existingPart107Doc()!.url" target="_blank" class="btn btn-sm btn-outline-success">
                      <i class="bi bi-eye me-1"></i>View
                    </a>
                  </div>
                </div>
                <input
                  type="file"
                  class="form-control"
                  id="part107File"
                  accept=".pdf,.jpg,.jpeg,.png"
                  (change)="onPart107FileSelected($event)">
                <small *ngIf="existingPart107Doc()" class="text-muted">Upload a new file to replace the existing document</small>
                <div *ngIf="part107File()" class="mt-2 small text-success">
                  <i class="bi bi-check-circle me-1"></i>
                  {{ part107File()!.name }}
                </div>
              </div>
            </div>
          </form>

          <div class="mt-3">
            <p class="small text-muted">
              <i class="bi bi-info-circle me-1"></i>
              Your certificate must be valid and not expired
            </p>
            <a href="https://www.faa.gov/uas/commercial_operators/become_a_drone_pilot" target="_blank" class="small">
              Don't have your Part 107 yet? Learn how to get certified
              <i class="bi bi-box-arrow-up-right ms-1"></i>
            </a>
          </div>
        </div>

        <!-- Step 3: Upload Medical Certificate (Optional) -->
        <div *ngIf="currentStep() === 3" class="step-content">
          <div class="step-header mb-3">
            <h6>{{ currentStepInfo().title }}</h6>
            <p class="text-muted">{{ currentStepInfo().subtitle }}</p>
            <div class="alert alert-secondary small">
              <i class="bi bi-info-circle me-1"></i>
              Optional - Skip if not applicable. If uploading, expiration date is required.
            </div>
          </div>

          <form [formGroup]="medicalForm">
            <div class="row g-3">
              <div class="col-md-6">
                <label for="medicalNumber" class="form-label">Certificate Number</label>
                <input
                  type="text"
                  class="form-control"
                  id="medicalNumber"
                  formControlName="certificateNumber">
              </div>
              <div class="col-md-6">
                <label for="medicalExpiration" class="form-label">
                  Expiration Date
                  <span *ngIf="medicalFile()" class="text-danger">*</span>
                </label>
                <input
                  type="date"
                  class="form-control"
                  id="medicalExpiration"
                  formControlName="expirationDate"
                  [class.is-invalid]="medicalFile() && !medicalForm.get('expirationDate')?.valid"
                  max="9999-12-31">
                <div *ngIf="medicalFile() && !medicalForm.get('expirationDate')?.valid" class="invalid-feedback">
                  Expiration date is required when uploading a certificate
                </div>
              </div>
              <div class="col-12">
                <label for="medicalFile" class="form-label">Upload Certificate (PDF, JPG, PNG)</label>
                <!-- Show existing document if present -->
                <div *ngIf="existingMedicalDoc() && !medicalFile()" class="alert alert-success py-2 mb-2">
                  <div class="d-flex align-items-center justify-content-between">
                    <div>
                      <i class="bi bi-file-earmark-check me-2"></i>
                      <span class="small">{{ existingMedicalDoc()!.fileName }}</span>
                    </div>
                    <a [href]="existingMedicalDoc()!.url" target="_blank" class="btn btn-sm btn-outline-success">
                      <i class="bi bi-eye me-1"></i>View
                    </a>
                  </div>
                </div>
                <input
                  type="file"
                  class="form-control"
                  id="medicalFile"
                  accept=".pdf,.jpg,.jpeg,.png"
                  (change)="onMedicalFileSelected($event)">
                <small *ngIf="existingMedicalDoc()" class="text-muted">Upload a new file to replace the existing document</small>
                <div *ngIf="medicalFile()" class="mt-2 small text-success">
                  <i class="bi bi-check-circle me-1"></i>
                  {{ medicalFile()!.name }}
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- Step 4: Upload Insurance Certificate (Optional) -->
        <div *ngIf="currentStep() === 4" class="step-content">
          <div class="step-header mb-3">
            <h6>{{ currentStepInfo().title }}</h6>
            <p class="text-muted">{{ currentStepInfo().subtitle }}</p>
            <div class="alert alert-secondary small">
              <i class="bi bi-info-circle me-1"></i>
              Optional - Skip if not required. If uploading, expiration date is required.
            </div>
          </div>

          <form [formGroup]="insuranceForm">
            <div class="row g-3">
              <div class="col-md-6">
                <label for="insurancePolicy" class="form-label">Policy Number</label>
                <input
                  type="text"
                  class="form-control"
                  id="insurancePolicy"
                  formControlName="policyNumber">
              </div>
              <div class="col-md-6">
                <label for="insuranceExpiration" class="form-label">
                  Expiration Date
                  <span *ngIf="insuranceFile()" class="text-danger">*</span>
                </label>
                <input
                  type="date"
                  class="form-control"
                  id="insuranceExpiration"
                  formControlName="expirationDate"
                  [class.is-invalid]="insuranceFile() && !insuranceForm.get('expirationDate')?.valid"
                  max="9999-12-31">
                <div *ngIf="insuranceFile() && !insuranceForm.get('expirationDate')?.valid" class="invalid-feedback">
                  Expiration date is required when uploading a certificate
                </div>
              </div>
              <div class="col-12">
                <label for="insuranceFile" class="form-label">Upload Certificate (PDF, JPG, PNG)</label>
                <!-- Show existing document if present -->
                <div *ngIf="existingInsuranceDoc() && !insuranceFile()" class="alert alert-success py-2 mb-2">
                  <div class="d-flex align-items-center justify-content-between">
                    <div>
                      <i class="bi bi-file-earmark-check me-2"></i>
                      <span class="small">{{ existingInsuranceDoc()!.fileName }}</span>
                    </div>
                    <a [href]="existingInsuranceDoc()!.url" target="_blank" class="btn btn-sm btn-outline-success">
                      <i class="bi bi-eye me-1"></i>View
                    </a>
                  </div>
                </div>
                <input
                  type="file"
                  class="form-control"
                  id="insuranceFile"
                  accept=".pdf,.jpg,.jpeg,.png"
                  (change)="onInsuranceFileSelected($event)">
                <small *ngIf="existingInsuranceDoc()" class="text-muted">Upload a new file to replace the existing document</small>
                <div *ngIf="insuranceFile()" class="mt-2 small text-success">
                  <i class="bi bi-check-circle me-1"></i>
                  {{ insuranceFile()!.name }}
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- Step 5: Upload State Pesticide License (Required) -->
        <div *ngIf="currentStep() === 5" class="step-content">
          <div class="step-header mb-3">
            <div class="d-flex align-items-center">
              <i class="bi bi-exclamation-triangle-fill text-warning me-2"></i>
              <h6 class="mb-0">{{ currentStepInfo().title }}</h6>
            </div>
            <p class="text-muted mt-2">{{ currentStepInfo().subtitle }}</p>
            <div class="alert alert-info small">
              <i class="bi bi-info-circle me-1"></i>
              Required for pesticide application operations.
            </div>
          </div>

          <form [formGroup]="stateLicenseForm">
            <div class="row g-3">
              <div class="col-md-6">
                <label for="stateLicenseNumber" class="form-label">License Number <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="stateLicenseNumber"
                  formControlName="licenseNumber">
              </div>
              <div class="col-md-6">
                <label for="stateLicenseState" class="form-label">State <span class="text-danger">*</span></label>
                <app-us-state-select inputId="stateLicenseState" formControlName="state" display="both"></app-us-state-select>
              </div>
              <div class="col-12">
                <label for="stateLicenseExpiration" class="form-label">Expiration Date <span class="text-danger">*</span></label>
                <input
                  type="date"
                  class="form-control"
                  id="stateLicenseExpiration"
                  formControlName="expirationDate"
                  max="9999-12-31">
              </div>
              <div class="col-12">
                <label for="stateLicenseFile" class="form-label">
                  Upload License (PDF, JPG, PNG)
                  <span *ngIf="!existingStateLicenseDoc()" class="text-danger">*</span>
                </label>
                <!-- Show existing document if present -->
                <div *ngIf="existingStateLicenseDoc() && !stateLicenseFile()" class="alert alert-success py-2 mb-2">
                  <div class="d-flex align-items-center justify-content-between">
                    <div>
                      <i class="bi bi-file-earmark-check me-2"></i>
                      <span class="small">{{ existingStateLicenseDoc()!.fileName }}</span>
                    </div>
                    <a [href]="existingStateLicenseDoc()!.url" target="_blank" class="btn btn-sm btn-outline-success">
                      <i class="bi bi-eye me-1"></i>View
                    </a>
                  </div>
                </div>
                <input
                  type="file"
                  class="form-control"
                  id="stateLicenseFile"
                  accept=".pdf,.jpg,.jpeg,.png"
                  (change)="onStateLicenseFileSelected($event)">
                <small *ngIf="existingStateLicenseDoc()" class="text-muted">Upload a new file to replace the existing document</small>
                <div *ngIf="stateLicenseFile()" class="mt-2 small text-success">
                  <i class="bi bi-check-circle me-1"></i>
                  {{ stateLicenseFile()!.name }}
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- Step 6: Review Safety Guidelines -->
        <div *ngIf="currentStep() === 6" class="step-content">
          <div class="step-header mb-3">
            <h6>{{ currentStepInfo().title }}</h6>
            <p class="text-muted">{{ currentStepInfo().subtitle }}</p>
          </div>

          <div class="card mb-3">
            <div class="card-body">
              <h6 class="card-title">Safety Guidelines</h6>
              <p class="card-text small">Please review the following safety guidelines before starting operations:</p>
              <ul class="small">
                <li>Always perform pre-flight checks before each flight</li>
                <li>Maintain visual line-of-sight with aircraft at all times</li>
                <li>Do not fly over people or moving vehicles</li>
                <li>Respect altitude limits and airspace restrictions</li>
                <li>Check weather conditions before flying</li>
                <li>Maintain proper aircraft maintenance and logs</li>
                <li>Follow all FAA regulations and local ordinances</li>
              </ul>
              <a href="https://www.faa.gov/uas" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-outline-primary">
                View FAA Safety Guidelines
                <i class="bi bi-box-arrow-up-right ms-1"></i>
              </a>
            </div>
          </div>

          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              id="safetyAck"
              [checked]="safetyAcknowledged()"
              (change)="safetyAcknowledged.set(!safetyAcknowledged())">
            <label class="form-check-label" for="safetyAck">
              I have reviewed and understand the safety guidelines <span class="text-danger">*</span>
            </label>
          </div>
        </div>

        <!-- Step 7: Complete -->
        <div *ngIf="currentStep() === 7" class="step-content text-center py-4">
          <div class="mb-3">
            <i class="bi bi-check-circle-fill text-success"></i>
          </div>
          <h4>Congratulations!</h4>
          <p class="text-muted">Your personal onboarding is complete. You're ready to start operations!</p>
          <div class="alert alert-success mt-3">
            <i class="bi bi-info-circle me-1"></i>
            You can now log flights, manage records, and access all team features.
          </div>
          <div class="alert alert-warning mt-3 text-start">
            <div class="d-flex align-items-start">
              <i class="bi bi-exclamation-triangle me-2 mt-1"></i>
              <div>
                <strong>Organization certifications still needed</strong>
                <p class="mb-2 small">Your organization may still require the following before pilots can operate:</p>
                <ul class="small mb-2 text-start">
                  <li>FAA Part 137 Certificate (Agricultural Aircraft Operations)</li>
                  <li>Liability Insurance</li>
                </ul>
                <p class="mb-0 small">
                  Visit the <a routerLink="/certifications" (click)="close()">Certifications page</a> to upload these.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-outline-secondary"
          (click)="previousStep()"
          [disabled]="currentStep() === 1 || isProcessing()">
          <i class="bi bi-arrow-left me-1"></i>
          Back
        </button>

        <button
          *ngIf="!currentStepInfo().isRequired && currentStep() !== totalSteps()"
          type="button"
          class="btn btn-outline-secondary"
          (click)="skipStep()"
          [disabled]="isProcessing()">
          Skip for Now
        </button>

        <button
          *ngIf="currentStep() < totalSteps()"
          type="button"
          class="btn btn-primary"
          (click)="nextStep()"
          [disabled]="!canProceed() || isProcessing()">
          <span *ngIf="isProcessing()" class="spinner-border spinner-border-sm me-1" role="status"></span>
          {{ currentStepInfo().isRequired && !canProceed() ? 'Required' : 'Continue' }}
          <i class="bi bi-arrow-right ms-1"></i>
        </button>

        <button
          *ngIf="currentStep() === totalSteps()"
          type="button"
          class="btn btn-success"
          (click)="close()">
          <i class="bi bi-check-circle me-1"></i>
          Done
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Modal Backdrop -->
<div *ngIf="isOpen()" class="modal-backdrop show"></div>
